/// <reference path="../registerCreditPaymentSectionBase.ts" />
module Meevo.Plugins.Register {
    'use strict';

    export class CareCreditPaymentsSection extends Meevo.Plugins.Register.RegisterCreditPaymentSectionBase {
        private readonly hostedPageId: Meevo.Model.HostedPageIdType = 'HostedPageCareCreditPayment';

        constructor(
            registerContext: Meevo.Plugins.Register.RegisterContext,
            private careCreditMerchantAccount: Meevo.Model.MerchantAccount
        ) {
            super(RegisterSectionTypeEnum.CareCreditPayment, registerContext, 'careCreditPayments', Register.RegisterSectionCatTypeEnum.Payment);
        }

        isHostedPage() {
            return !!this.careCreditMerchantAccount;
        }

        isHostedPageActive() {
            return super.isHostedPageActive(this.hostedPageId);
        }

        protected getNewPayment() {
            return super.getNewPayment(Model.PaymentCategoryEnum.CareCredit);
        }

        reset() {
            this.payment = this.getNewPayment();

            this.addBalanceAsPayment();

            this.hostedPageService.reset(this.hostedPageId);

            this.registerContext.hideCheckoutButtons = true;
        }

        addPaymentIsDisabled() {
            return this.payment.amount === 0;
        }

        checkCareCreditStatus() {
            const paymentLine: Model.SaleCreditCardPayment = _(this.registerContext.sale.paymentLines).findWhere({ isPending: true });

            if (!paymentLine) {
                return false;
            }

            this.busyService.start();
            this.registerContext.getPaymentRequestStatus(paymentLine, true)
                .then((response) => {
                    const paymentRequest = response.data;

                    if (paymentRequest && paymentRequest.paymentRequestStatus === Meevo.Model.PaymentRequestStatusEnum.Approved) {
                        this.registerContext.refreshPaymentRequest(paymentLine, false)
                            .then(() => {
                                this.busyService.stop();

                                // paymentLine has been updated by refreshPaymentRequest - check for Change Due prompt
                                this.processApprovedPayment(paymentLine);
                            })
                            .catch(() => {
                                this.busyService.stop();
                            });
                    } else if (paymentRequest && 
                        paymentRequest.paymentRequestStatus === Meevo.Model.PaymentRequestStatusEnum.Failed &&
                        paymentRequest.processorResponseCode !== Meevo.Model.ProcessorResponseCodeEnum.NotDefined) {
                        this.busyService.stop();

                        this.registerContext.removePaymentLine(paymentLine);

                        const message = this.$translate.instant(paymentRequest.paymentProcessorResponseMessageKey);

                        this.modalService.alert(message).then(() => {
                            return this.returnToOptions();
                        });
                    } else {
                        this.busyService.stop();

                        const incompleteProcessMessage = this.registerContext.isCreditMode
                            ? 'MESSAGE_CARECREDIT_INCOMPLETE_PROCESS_REFUND'
                            : 'MESSAGE_CARECREDIT_INCOMPLETE_PROCESS';

                        this.modalService.alert(incompleteProcessMessage);
                    }
                })
                .catch(() => {
                    this.busyService.stop();
                });
        }

        private setSaleOnHoldId(onHoldId, holdType?) {
            this.registerContext.setSaleOnHoldId(onHoldId, holdType);
            this.registerContext.sale.onHoldIds[onHoldId] = holdType || Meevo.Model.HoldTypeEnum.Recovery;
            this.registerContext.updateSelfPaymentWithOnHoldId(onHoldId, false);
        }

        // this is called from register.context selectSection method when selecting "CareCredit" payment tile
        processCareCredit() {
            this.processFull();
        }

        processFull() {
            if (!this.payment) {
                this.payment = this.getNewPayment();

                this.addBalanceAsPayment();
            }

            this.showAddPaymentButton = false;
            this.addPayment();
        }

        addPayment() {
            this.processPayment();
        }

        validate() {
            if (!this.validateAmount()) {
                return false;
            }

            if (!this.careCreditMerchantAccount) {
                this.modalService.alert("MESSAGE_MISSING_MERCHANT_ACCOUNT");
                return false;
            }

            if (this.registerContext.hasPendingPayment()) {
                this.modalService.alert("MESSAGE_PENDING_CREDIT_CARD_PAYMENT");
                return false;
            }

            return true;
        }

        protected returnToOptions() {
            this.registerContext.registerCareCreditService.updateCareCreditPaymentType(this.registerContext.paymentCats, this.registerContext.sale);

            return super.returnToOptions();
        }

        private processPayment() {
            if (!this.validate()) {
                return null;
            }

            const continueExecution = (response: any = null) => {
                this.busyService.start("LABEL_CARECREDIT_PENDING_PROCESSING", true);

                // create a payment record and have credit card processor process the payment
                this.payment.requestedAmount = parseFloat(this.payment.amount.toString());

                const request = {
                    paymentRequest: this.payment,
                    merchantAccountId: this.careCreditMerchantAccount.id,
                    sale: this.registerContext.sale,
                    transactionSetupId: null,
                    transactionId: null,
                    paymentRequestId: null,
                    onHoldId: null,
                };

                request.transactionSetupId = response.transactionSetupId;
                request.transactionId = response.transactionId;
                request.paymentRequestId = response.paymentRequestId;
                request.onHoldId = response.onHoldId;

                if (!request.paymentRequestId) {
                    const automaticChangeDue = true;
                    // add the payment to the smart receipt
                    super.addPayment(false, automaticChangeDue);
                }

                return this.dataService
                    .customMethodData(`api/Drawer/${this.registerContext.selectedDrawer.id}/PaymentRequest`, "POST", request, { skipUnknownError: true })
                    .then(this.processPaymentSuccessCallback, (response) => this.processPaymentErrorCallback(response, true))
                    .finally(() => {
                        this.busyService.stop();
                    });
            };

            const request: Meevo.Components.HostedPage.IHostedPageRequest = {
                drawerId: this.registerContext.selectedDrawer.id,
                merchantAccountId: this.careCreditMerchantAccount.id,
                merchantProviderId: this.careCreditMerchantAccount.merchantProviderId,
                transactionAmount: this.payment.amount,
                sale: this.registerContext.sale,
                paymentLineTempId: this.payment.tempId,
                tokenCreatedCallback: (setupId: string, paymentRequestId: any, onHoldId: any) => {
                    this.payment.transactionSetupId = setupId;
                    this.payment.paymentRequestId = paymentRequestId;
                    if (onHoldId) {
                        this.payment.onHoldId = onHoldId;
                        this.setSaleOnHoldId(onHoldId);
                    }
                }
            };

            const deferred = this.$q.defer();

            super.addPayment(false, false);

            this.hostedPageService.process(request, this.hostedPageId)
                .then((response: any) => {
                    if (response === 'no_info_found') {
                        this.registerContext.removePendingTicketPaymentLine();

                        this.reset();

                        this.returnToOptions();
                    } else {
                        this.payment.transactionSetupId = null;

                        continueExecution(response);
                    }

                    deferred.resolve();
                })
                .catch((response: any) => {
                    this.processPaymentErrorCallback(response);

                    deferred.reject();
                });

            return deferred.promise;
        }

        processApprovedPayment(paymentLine: Model.SaleCreditCardPayment, paymentRequest: Model.PaymentRequest = null) {
            paymentLine.transactionSetupId = null;

            if (paymentRequest) {
                this.registerContext.updatePaymentLineWithPaymentRequest(paymentRequest, paymentLine);
            }

            this.registerContext.recalculateChangeDue();

            this.returnToOptions().finally(() => {
                if (this.registerContext.checkIfPaymentIsEnough()) {
                    this.registerContext.finishSection();
                }
            });
        }

        processPaymentSuccessCallback = (response) => {
            if (response.status == 409) { // conflict
                this.processPaymentErrorCallback(response);
                return;
            }

            let paymentLine: Model.SaleCreditCardPayment = _.findWhere(this.registerContext.sale.paymentLines, { isPending: true });

            let paymentRequest: Model.PaymentRequest = response.data;

            if (paymentRequest.paymentRequestStatus === Model.PaymentRequestStatusEnum.Approved) {
                return this.processApprovedPayment(paymentLine, paymentRequest);
            }

            if (paymentRequest.onHoldId) {
                this.setSaleOnHoldId(paymentRequest.onHoldId, paymentRequest.holdType);
            }

            this.registerContext.removeTicketPaymentLine(paymentLine);

            this.addBalanceAsPayment();

            if (paymentRequest.creditCardProcessorStatus) {
                let message = this.getProcessedPaymentMessage(paymentRequest);
                this.busyService.stop();
                return this.modalService.alert(message, Components.Modal.ModalTranslationModes.DoNotTranslate).then(() => {
                    this.busyService.start();
                });
            }
        }
    }
}